import {
  WorkflowNodeData,
  WorkflowNodeSetting,
  SettingParam,
  SettingParamTypes,
  FlowMode,
  AssistantType,
} from "@/types/node";
import React, {useEffect, useState, useRef} from "react";
import {Node} from "@xyflow/react";
import {useNodeUpdater} from "../hooks/useNodeUpdater";
import {
  SettingSelect,
  SettingInput,
  SettingTextarea,
  SettingPrompt,
  SettingAction,
  SettingModal,
  SettingNodeSelect,
  SettingCapture,
} from "@/components/settings";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import {getSettingValueByKey} from "@/utils/index";
import {
  Settings,
  Settings2,
  Sparkles,
  Zap,
  CircleHelp,
  ListChecks,
  OctagonX,
  Hand,
  UserCog,
  BookOpen,
  OctagonAlert,
  Braces,
  ArrowDown,
} from "lucide-react";
import {useSave} from "@/contexts/SaveContext";
import {CaptureVariableIcon} from "@/components/icons/CaptureVariableIcon";

export const iconsMap = {
  Settings: Settings,
  Zap: Zap,
  Sparkles: Sparkles,
  Settings2: Settings2,
  CircleHelp: CircleHelp,
  ListChecks: ListChecks,
  OctagonX: OctagonX,
  Hand: Hand,
  UserCog: UserCog,
  BookOpen: BookOpen,
  OctagonAlert: OctagonAlert,
  Braces: CaptureVariableIcon,
};

export const DynamicIcon = ({
  iconName,
  className,
}: {
  iconName: keyof typeof iconsMap;
  className: string;
}) => {
  const IconComponent = iconsMap[iconName];
  return IconComponent ? <IconComponent className={className} /> : null;
};

export const SettingComponentMap = {
  [SettingParamTypes.SELECT]: SettingSelect,
  [SettingParamTypes.NODE_SELECT]: SettingNodeSelect,
  [SettingParamTypes.INPUT]: SettingInput,
  [SettingParamTypes.TEXTAREA]: SettingTextarea,
  [SettingParamTypes.PROMPT]: SettingPrompt,
  [SettingParamTypes.ACTIONS]: SettingAction,
  [SettingParamTypes.MODAL]: SettingModal,
  [SettingParamTypes.CAPTURE]: SettingCapture,
} as Record<SettingParamTypes, React.ComponentType<any>>;

export const renderSetting = (
  param: SettingParam,
  index: number,
  selectedNodeId: string,
  assistantType: AssistantType,
  handleChange: (param: SettingParam, value: string) => void,
) => {
  const {handleSave, flowMode} = useSave();
  const SettingComponent = SettingComponentMap[param.type];

  return SettingComponent ? (
    <SettingComponent
      key={param.key}
      param={param}
      index={index}
      selectedNodeId={selectedNodeId}
      handleChange={(param: SettingParam, value: string) => {
        handleChange(param, value);
        handleSave();
      }}
      selectOptions={{}}
      placeholder={param.placeholder}
      disabled={flowMode === FlowMode.LIVE}
      assistantType={assistantType}
    />
  ) : null;
};

const SettingsGroup = ({
  settings,
  selectedNode,
  assistantType,
  takeSnapshot,
  updateNodeSettingParam,
}: {
  settings: WorkflowNodeSetting[];
  selectedNode: Node;
  assistantType: AssistantType;
  takeSnapshot: () => void;
  updateNodeSettingParam: (id: string, key: string, value: any) => void;
}) => {
  return (
    <>
      {settings.map((setting, index) => (
        <div key={index}>
          {setting.params.map((param, i) =>
            renderSetting(
              param,
              i,
              selectedNode.id,
              assistantType,
              (param, value) => {
                updateNodeSettingParam(selectedNode.id, param.key, value);
                takeSnapshot();
              },
            ),
          )}
        </div>
      ))}
    </>
  );
};

export const NodeSettings: React.FC<{
  selectedNode: Node;
  nodes: Node[];
  takeSnapshot: () => void;
  assistantType: AssistantType;
}> = React.memo(({selectedNode, nodes, takeSnapshot, assistantType}) => {
  const settingsRef = useRef(null);
  const [openedIndex, setOpenedIndex] = useState(0);

  useEffect(() => {
    // Autofocus first field in settings
    if (settingsRef.current && openedIndex !== null) {
      const accordionItem = settingsRef.current.querySelector(
        `[data-accordion-index="${openedIndex}"]`,
      );
      const settingWrapperRef = accordionItem ?? settingsRef.current;
      const firstField = settingWrapperRef?.querySelector(
        "input, select, textarea, button, div[contenteditable]",
      );

      if (firstField) firstField.focus();
      setOpenedIndex(null);
    }
  }, [openedIndex]);

  const {updateNodeSettingParam} = useNodeUpdater();
  const nodeData = selectedNode.data as WorkflowNodeData;
  const settingsTitle =
    getSettingValueByKey(nodeData.settings ?? [], "name") ||
    nodeData.settingsTitle ||
    "New Stage";

  const settings = nodeData.settings as WorkflowNodeSetting[];

  return (
    <div
      ref={settingsRef}
      className="flex flex-col max-h-full h-full"
      key={selectedNode.id}
    >
      <h2 className="text-lg font-semibold border-b p-5 text-ellipsis overflow-hidden whitespace-nowrap">
        {settingsTitle?.toString() || "New Stage"}
      </h2>
      <div className="mb-3 overflow-y-auto flex-grow">
        {settings.length === 1 ? (
          <>
            {nodeData.settings && nodeData.settings[0].description && (
              <p className="text-secondary px-5 text-sm mt-3">
                {nodeData.settings[0].description}
              </p>
            )}
            <div className="p-5">
              <SettingsGroup
                settings={settings}
                selectedNode={selectedNode}
                assistantType={assistantType}
                takeSnapshot={takeSnapshot}
                updateNodeSettingParam={updateNodeSettingParam}
              />
            </div>
          </>
        ) : (
          settings.map((setting, index) => {
            const hasDefinedParam = setting.params.some(
              param =>
                param.value !== undefined &&
                param.value !== "" &&
                (!Array.isArray(param.value) || param.value.length > 0),
            );
            const isExpanded = setting.expandedSetting;
            return !isExpanded ? (
              <Accordion
                type="single"
                collapsible
                key={index}
                defaultValue={`item-0`}
              >
                <AccordionItem
                  value={`item-${index}`}
                  className="overflow-visible"
                >
                  <AccordionTrigger
                    onClick={() => {
                      setOpenedIndex(index);
                    }}
                  >
                    {setting.icon && (
                      <DynamicIcon
                        iconName={setting.icon}
                        className={`mr-2 h-5 w-5 ${
                          hasDefinedParam
                            ? "text-primary-accent"
                            : "text-secondary"
                        }`}
                      />
                    )}
                    <h4 className="font-semibold">{setting.title}</h4>
                  </AccordionTrigger>
                  <AccordionContent data-accordion-index={index}>
                    {setting.description && (
                      <p className="mb-3 text-secondary">
                        {setting.description}
                      </p>
                    )}
                    <div>
                      <SettingsGroup
                        settings={[setting]}
                        selectedNode={selectedNode}
                        assistantType={assistantType}
                        takeSnapshot={takeSnapshot}
                        updateNodeSettingParam={updateNodeSettingParam}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <div className="p-5 border-b" key={index}>
                <SettingsGroup
                  settings={[setting]}
                  selectedNode={selectedNode}
                  assistantType={assistantType}
                  takeSnapshot={takeSnapshot}
                  updateNodeSettingParam={updateNodeSettingParam}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

NodeSettings.displayName = "NodeSettings";
